import Image from 'next/image';
import { type ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useUser from '@alltrails/context/hooks/useUser';
import { BREAKPOINT_SM_SIZE } from '@alltrails/denali/tokens';
import type { SubscriptionFeatures } from '@alltrails/types/SubscriptionFeatures';
import type { Upsell } from '@alltrails/types/Upsell';
import type { User } from '@alltrails/types/User';
import BaseUpsellStep from '../BaseUpsell/BaseUpsellStep';
import GenericUpsellContent from '../GenericUpsellContent/GenericUpsellContent';
import getServerUpsellCopy from '../getServerUpsellCopy';
import getUpsellCtaText from '../getUpsellCtaText';
import { plusUpsellHeading, plusUpsellPromoEligibleHeading } from './GenericPlusUpsell';
import headerImage from './images/plus-header.avif';

type Props = {
  ctaText?: ReactNode;
  navigateTo: (modalKey: 'checkout' | 'upsell') => void;
  onCloseRequest: () => void;
  onSuccess?: (user: User) => void;
  promoEligibleCtaText?: ReactNode;
  promoEligibleSubCopy?: ReactNode;
  promoEligibleSubheading?: ReactNode;
  // Server-driven copy for this trigger. Each field it provides wins over the props above.
  serverUpsellContent?: Upsell;
  subCopy?: ReactNode;
  subheading?: ReactNode;
  subscriptionFeatures?: SubscriptionFeatures;
  upsellTrigger: UpsellTrigger;
};

export default function GenericPlusUpsellStep({
  ctaText,
  navigateTo,
  onCloseRequest,
  onSuccess,
  promoEligibleCtaText,
  promoEligibleSubCopy,
  promoEligibleSubheading,
  serverUpsellContent,
  subCopy,
  subheading,
  subscriptionFeatures,
  upsellTrigger
}: Props) {
  const user = useUser();
  const isPromoEligible = !user || user.isPromoEligible;
  const serverCopy = getServerUpsellCopy({ isPromoEligible, serverUpsellContent });
  const resolvedCtaText = serverCopy.ctaText || (isPromoEligible ? promoEligibleCtaText : ctaText);
  const resolvedSubCopy = serverCopy.subCopy || (isPromoEligible ? promoEligibleSubCopy : subCopy);

  useEffect(() => {
    // Scroll to top in case the user scrolled down in the previous step.
    const modalScrollContainer = document.querySelector('#modal-scroll-container');
    modalScrollContainer?.scrollTo(0, 0);
  }, []);

  return (
    <BaseUpsellStep
      caption={resolvedSubCopy}
      headerImage={<Image alt="" sizes={`(max-width: ${BREAKPOINT_SM_SIZE}px) 100vw, 480px`} src={headerImage} />}
      headerImageV3
      heading={serverCopy.heading || plusUpsellHeading}
      navigateTo={navigateTo}
      onCloseRequest={() => {
        if (user) {
          onSuccess?.(user);
        }

        onCloseRequest();
      }}
      primaryButtonText={resolvedCtaText ?? getUpsellCtaText({ tier: 'plus', user })}
      primaryButtonVariant="accent"
      productsAvailable="plus"
      productType="plus"
      promoEligibleHeading={serverCopy.promoEligibleHeading || plusUpsellPromoEligibleHeading}
      upsellTrigger={upsellTrigger}
    >
      <GenericUpsellContent
        featureListLabel={<FormattedMessage defaultMessage="Plus also includes" />}
        heading={serverCopy.heading || plusUpsellHeading}
        isPromoEligible={isPromoEligible}
        promoEligibleHeading={serverCopy.promoEligibleHeading || plusUpsellPromoEligibleHeading}
        promoEligibleSubheading={serverCopy.promoEligibleSubheading || promoEligibleSubheading}
        subheading={serverCopy.subheading || subheading}
        subscriptionFeatures={serverCopy.subscriptionFeatures || subscriptionFeatures}
        tier="plus"
        upsellTrigger={upsellTrigger}
      />
    </BaseUpsellStep>
  );
}
